import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { LoadingSpinner } from '../../components/layout/SharedComponents';
import api, { getApiErrorMessage } from '../../services/api';
import { toast } from 'sonner';
import {
  Award, CheckCircle, XCircle, FileText, Send, ArrowRight, Users, Clock, MessageSquare,
} from 'lucide-react';
import { SortableTableHeader, useSortState, sortData } from '../../components/SortableTableHeader';

export function AwardTab({ eventRef, form, onClose }) {
  const [loading, setLoading] = useState(true);
  const [suppliers, setSuppliers] = useState([]);
  const [awardDecision, setAwardDecision] = useState(null);
  const [awards, setAwards] = useState([]);
  const [notes, setNotes] = useState('');
  const [createReq, setCreateReq] = useState(true);
  const [notifySuppliers, setNotifySuppliers] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const isAwarded = form.status === 'AWARDED';
  const canAward = ['EVALUATION', 'AWARD_PENDING', 'CLOSED'].includes(form.status);
  const currency = form.currency || 'USD';

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [supRes, awardRes] = await Promise.all([
          api.get(`/api/rfx/workflow/suppliers/${eventRef}`),
          api.get(`/api/rfx/workflow/award/${eventRef}`).catch(() => ({ data: null })),
        ]);
        setSuppliers(supRes.data.data || []);
        if (awardRes.data && awardRes.data.event_ref) {
          setAwardDecision(awardRes.data);
        }
      } catch { }
      setLoading(false);
    };
    load();
  }, [eventRef]);

  const toggleAward = (supplierCode, supplierName) => {
    const existing = awards.find(a => a.supplier_code === supplierCode);
    if (existing) {
      setAwards(awards.filter(a => a.supplier_code !== supplierCode));
    } else {
      // Build items from auction items
      const items = (form.auction_items || []).map(item => ({
        item_id: item.item_id,
        description: item.description,
        quantity: item.quantity,
        uom: item.uom,
        awarded_price: item.estimated_price || 0,
      }));
      setAwards([...awards, { supplier_code: supplierCode, supplier_name: supplierName, items, reason: '' }]);
    }
  };

  const updateAwardReason = (code, reason) => {
    setAwards(awards.map(a => a.supplier_code === code ? { ...a, reason } : a));
  };

  const updateAwardItemPrice = (supplierCode, itemId, price) => {
    setAwards(awards.map(a => {
      if (a.supplier_code !== supplierCode) return a;
      return { ...a, items: a.items.map(i => i.item_id === itemId ? { ...i, awarded_price: parseFloat(price) || 0 } : i) };
    }));
  };

  const handleAward = async () => {
    if (awards.length === 0) { toast.error('Select at least one supplier to award'); return; }
    setSubmitting(true);
    try {
      const r = await api.post(`/api/rfx/workflow/award/${eventRef}`, {
        awards,
        notes,
        create_requisition: createReq,
        notify_suppliers: notifySuppliers,
      });
      toast.success(r.data.message);
      if (r.data.requisition_number) {
        toast.success(`Requisition ${r.data.requisition_number} created`);
      }
      onClose();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Award failed')); }
    setSubmitting(false);
  };

  const handleTransition = async (status, reason = '') => {
    try {
      const r = await api.post(`/api/rfx/workflow/transition/${eventRef}`, { status, reason });
      toast.success(r.data.message);
      onClose();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Transition failed')); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><LoadingSpinner /></div>;

  // Already awarded — show decision
  if (isAwarded && awardDecision) {
    return (
      <div className="space-y-4 py-2" data-testid="award-tab-awarded">
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Award className="w-5 h-5 text-emerald-600" />
            <h3 className="font-bold text-emerald-900">Event Awarded</h3>
            <span className="text-xs text-emerald-600 ml-auto">{awardDecision.awarded_at}</span>
          </div>
          <p className="text-sm text-emerald-800">Awarded by {awardDecision.awarded_by}</p>
          {awardDecision.notes && <p className="text-xs text-emerald-700 mt-1 italic">{awardDecision.notes}</p>}
        </div>

        {(awardDecision.awards || []).map((a, idx) => (
          <div key={idx} className="border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              <span className="font-semibold text-sm">{a.supplier_name || a.supplier_code}</span>
              <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Awarded</Badge>
            </div>
            {a.reason && <p className="text-xs text-slate-500 mb-2">Reason: {a.reason}</p>}
            {a.items?.length > 0 && (
              <table className="w-full text-xs border rounded">
                <thead><tr className="bg-slate-50 border-b">
                  <th className="px-2 py-1 text-left">Item</th>
                  <th className="px-2 py-1 text-right">Qty</th>
                  <th className="px-2 py-1 text-right">Price ({currency})</th>
                  <th className="px-2 py-1 text-right">Total ({currency})</th>
                </tr></thead>
                <tbody>
                  {a.items.map((item, i) => (
                    <tr key={i} className="border-b">
                      <td className="px-2 py-1">{item.description || item.item_id}</td>
                      <td className="px-2 py-1 text-right">{item.quantity || item.qty}</td>
                      <td className="px-2 py-1 text-right font-mono">{(item.awarded_price || item.price || 0).toLocaleString()}</td>
                      <td className="px-2 py-1 text-right font-mono">{((item.quantity || item.qty || 1) * (item.awarded_price || item.price || 0)).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}

        <div className="border rounded-lg p-4">
          <h4 className="text-sm font-semibold mb-2">Supplier Outcomes</h4>
          <div className="space-y-1">
            {suppliers.map(sp => (
              <div key={sp.supplier_code} className="flex items-center justify-between text-xs py-1 border-b last:border-0">
                <span>{sp.supplier_name || sp.supplier_code}</span>
                <Badge className={`text-[10px] ${sp.state === 'AWARDED' ? 'bg-emerald-100 text-emerald-700' : sp.state === 'NOT_AWARDED' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>
                  {sp.state}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Award form
  return (
    <div className="space-y-4 py-2" data-testid="award-tab">
      {/* Status & transition controls */}
      <div className="flex items-center gap-2 bg-slate-50 border rounded-lg p-3">
        <Badge className="text-xs">{form.status}</Badge>
        <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
        {form.status === 'OPEN' && (
          <Button size="sm" variant="outline" onClick={() => handleTransition('CLOSED', 'Closing for evaluation')}>
            <Clock className="w-3 h-3 mr-1" /> Close for Evaluation
          </Button>
        )}
        {form.status === 'CLOSED' && (
          <Button size="sm" variant="outline" onClick={() => handleTransition('EVALUATION', 'Beginning evaluation')}>
            <Users className="w-3 h-3 mr-1" /> Begin Evaluation
          </Button>
        )}
        {form.status === 'EVALUATION' && (
          <Button size="sm" variant="outline" onClick={() => handleTransition('AWARD_PENDING', 'Preparing award')}>
            <Award className="w-3 h-3 mr-1" /> Move to Award Pending
          </Button>
        )}
        {!canAward && (
          <span className="text-xs text-slate-500 ml-2">Progress through evaluation stages to enable award</span>
        )}
      </div>

      {/* Supplier selection for award */}
      {canAward && (
        <>
          <div>
            <h3 className="font-semibold text-sm mb-2">Select Supplier(s) to Award</h3>
            <p className="text-xs text-slate-500 mb-3">Choose one or more suppliers. For multi-supplier awards, you can assign different items to each supplier.</p>
            <div className="space-y-2">
              {suppliers.filter(s => ['SUBMITTED', 'SHORTLISTED'].includes(s.state)).map(sp => {
                const isSelected = awards.some(a => a.supplier_code === sp.supplier_code);
                return (
                  <div key={sp.supplier_code} className={`border rounded-lg p-3 cursor-pointer transition-colors ${isSelected ? 'border-emerald-400 bg-emerald-50 ring-1 ring-emerald-300' : 'hover:border-slate-300'}`}
                    onClick={() => toggleAward(sp.supplier_code, sp.supplier_name)} data-testid={`award-supplier-${sp.supplier_code}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isSelected ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300'}`}>
                        {isSelected && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                      </div>
                      <div className="flex-1">
                        <span className="text-sm font-medium">{sp.supplier_name || sp.supplier_code}</span>
                        <Badge className={`ml-2 text-[10px] ${sp.state === 'SHORTLISTED' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>{sp.state}</Badge>
                      </div>
                    </div>

                    {isSelected && (
                      <div className="mt-3 pl-8 space-y-2" onClick={e => e.stopPropagation()}>
                        <div>
                          <Label className="text-xs">Award Reason</Label>
                          <Input value={awards.find(a => a.supplier_code === sp.supplier_code)?.reason || ''}
                            onChange={e => updateAwardReason(sp.supplier_code, e.target.value)}
                            placeholder="Reason for selecting this supplier..." className="h-8 text-xs" />
                        </div>
                        {(form.auction_items || []).length > 0 && (
                          <div>
                            <Label className="text-xs">Awarded Prices ({currency})</Label>
                            <div className="border rounded text-xs overflow-hidden mt-1">
                              {(form.auction_items || []).map(item => (
                                <div key={item.item_id} className="flex items-center justify-between px-2 py-1.5 border-b last:border-0">
                                  <span className="flex-1 truncate">{item.description}</span>
                                  <span className="text-slate-400 mx-2">Qty: {item.quantity}</span>
                                  <Input type="number" placeholder="0.00"
                                    value={awards.find(a => a.supplier_code === sp.supplier_code)?.items?.find(i => i.item_id === item.item_id)?.awarded_price || ''}
                                    onChange={e => updateAwardItemPrice(sp.supplier_code, item.item_id, e.target.value)}
                                    className="h-7 w-24 text-xs text-right" />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {suppliers.filter(s => ['SUBMITTED', 'SHORTLISTED'].includes(s.state)).length === 0 && (
                <p className="text-xs text-slate-400 py-4 text-center">No suppliers with SUBMITTED or SHORTLISTED status. Ensure suppliers have responded before awarding.</p>
              )}
            </div>
          </div>

          <div className="border rounded-lg p-4 space-y-3">
            <div>
              <Label>Award Notes</Label>
              <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Document the award rationale..." className="text-sm" />
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={createReq} onCheckedChange={setCreateReq} />
                <FileText className="w-3.5 h-3.5 text-slate-500" /> Auto-create requisition
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={notifySuppliers} onCheckedChange={setNotifySuppliers} />
                <Send className="w-3.5 h-3.5 text-slate-500" /> Notify all suppliers
              </label>
            </div>
            <Button onClick={handleAward} disabled={submitting || awards.length === 0} className="bg-emerald-600 hover:bg-emerald-700 w-full" data-testid="confirm-award-btn">
              <Award className="w-4 h-4 mr-2" /> {submitting ? 'Processing Award...' : `Award to ${awards.length} Supplier(s)`}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export function CommunicationsTab({ eventRef }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [newSubject, setNewSubject] = useState('');
  const [msgType, setMsgType] = useState('notice');

  useEffect(() => {
    const load = async () => {
      try {
        const r = await api.get(`/api/rfx/workflow/communications/${eventRef}`);
        setData(r.data);
      } catch { }
      setLoading(false);
    };
    load();
  }, [eventRef]);

  const postClarification = async () => {
    if (!newMessage.trim()) return;
    try {
      await api.post(`/api/rfx/workflow/clarifications/${eventRef}`, {
        type: msgType, subject: newSubject, message: newMessage, visible_to_all: true,
      });
      toast.success('Posted');
      setNewMessage(''); setNewSubject('');
      const r = await api.get(`/api/rfx/workflow/communications/${eventRef}`);
      setData(r.data);
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed')); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><LoadingSpinner /></div>;
  if (!data) return <p className="text-sm text-slate-400 py-8 text-center">Unable to load communications.</p>;

  // Build unified timeline
  const timeline = [];
  for (const h of (data.status_history || [])) {
    timeline.push({ type: 'state', label: `${h.from_status} → ${h.to_status}`, detail: h.reason, by: h.changed_by, at: h.changed_at, color: 'text-blue-600' });
  }
  for (const c of (data.clarifications || [])) {
    timeline.push({ type: 'clarification', label: c.subject || 'Clarification', detail: c.message, by: c.posted_by, at: c.posted_at, color: 'text-amber-600', subtype: c.type });
  }
  for (const d of (data.document_changes || [])) {
    timeline.push({ type: 'document', label: `${d.file_name} v${d.version}`, detail: d.change_description, by: d.changed_by, at: d.changed_at, color: 'text-violet-600' });
  }
  for (const sp of (data.supplier_participation || [])) {
    for (const h of (sp.state_history || [])) {
      timeline.push({ type: 'supplier', label: `${sp.supplier_name}: ${h.from} → ${h.to}`, detail: h.reason || '', by: h.by, at: h.at, color: 'text-indigo-600' });
    }
  }
  timeline.sort((a, b) => (b.at || '').localeCompare(a.at || ''));

  return (
    <div className="space-y-4 py-2" data-testid="communications-tab">
      {/* Post new clarification */}
      <div className="border rounded-lg p-4 bg-slate-50/50">
        <h3 className="font-semibold text-sm mb-2 flex items-center gap-1.5"><MessageSquare className="w-4 h-4 text-slate-500" /> Post Clarification / Notice</h3>
        <div className="grid grid-cols-4 gap-2 mb-2">
          <div className="col-span-1">
            <select value={msgType} onChange={e => setMsgType(e.target.value)} className="h-8 w-full rounded border text-xs px-2">
              <option value="notice">Notice</option>
              <option value="answer">Answer</option>
              <option value="question">Internal Q</option>
            </select>
          </div>
          <div className="col-span-3">
            <Input value={newSubject} onChange={e => setNewSubject(e.target.value)} placeholder="Subject..." className="h-8 text-xs" />
          </div>
        </div>
        <div className="flex gap-2">
          <Input value={newMessage} onChange={e => setNewMessage(e.target.value)} placeholder="Type message..." className="h-8 text-xs flex-1" />
          <Button size="sm" onClick={postClarification} disabled={!newMessage.trim()} className="bg-indigo-600 hover:bg-indigo-700">
            <Send className="w-3.5 h-3.5 mr-1" /> Post
          </Button>
        </div>
      </div>

      {/* Supplier states */}
      {(data.supplier_participation || []).length > 0 && (
        <div className="border rounded-lg p-4">
          <h3 className="font-semibold text-sm mb-2">Supplier Participation</h3>
          <div className="space-y-1">
            {(data.supplier_participation || []).map(sp => (
              <div key={sp.supplier_code} className="flex items-center justify-between text-xs py-1.5 border-b last:border-0">
                <span className="font-medium">{sp.supplier_name || sp.supplier_code}</span>
                <div className="flex items-center gap-2">
                  <Badge className={`text-[9px] ${
                    sp.state === 'AWARDED' ? 'bg-emerald-100 text-emerald-700' :
                    sp.state === 'NOT_AWARDED' ? 'bg-red-100 text-red-700' :
                    sp.state === 'SHORTLISTED' ? 'bg-blue-100 text-blue-700' :
                    sp.state === 'SUBMITTED' ? 'bg-violet-100 text-violet-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>{sp.state}</Badge>
                  <span className="text-slate-400">{sp.invited_at ? new Date(sp.invited_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'America/Los_Angeles' }) : ''}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="border rounded-lg p-4">
        <h3 className="font-semibold text-sm mb-3">Activity Timeline</h3>
        {timeline.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-4">No activity recorded yet.</p>
        ) : (
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {timeline.map((item, i) => (
              <div key={i} className="flex gap-3 text-xs py-1.5 border-b last:border-0">
                <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${item.color.replace('text-', 'bg-')}`} />
                <div className="flex-1 min-w-0">
                  <span className={`font-semibold ${item.color}`}>{item.label}</span>
                  {item.detail && <p className="text-slate-500 truncate">{item.detail}</p>}
                </div>
                <div className="text-right flex-shrink-0 text-slate-400">
                  <p>{item.by}</p>
                  <p>{item.at ? new Date(item.at).toLocaleString('en-GB', {day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit'}) : ''}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
